import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Archive, ArchiveRestore, Database, Loader2, MessageSquarePlus, Pin, PinOff, Search, Send, Sparkles } from 'lucide-react';
import { AnalystApiError, analystApi, type ConversationView, type RunListItem, type SearchOption } from '../services/api';
import type { DataScope, MetricFilterSet } from '../shared/analyst/run';
import { DatasetPanel } from '../components/analyst/DatasetPanel';
import { MetricFiltersPanel } from '../components/analyst/MetricFiltersPanel';
import { rangeValid } from '../components/analyst/metricFilters';
import { RunCard } from '../components/analyst/RunCard';
import { ScopePicker } from '../components/analyst/ScopePicker';
import { describeScope, emptyScope, scopeReady, scopeToInput, type ScopeDraft } from '../components/analyst/scope';

/**
 * Agente Analista (agentanalyst.md §13.1): conversazioni, thread di domande e risposte,
 * composer con perimetro obbligatorio. Le domande successive riusano il perimetro (e il
 * dataset) della conversazione finché l'utente non lo cambia.
 */

function draftOf(scope: DataScope): ScopeDraft {
  return scope.kind === 'dataset'
    ? { kind: 'dataset', datasetId: scope.datasetId, label: 'export della conversazione' }
    : { kind: 'pulsar', searchId: scope.searchId, from: scope.from, to: scope.to, sources: scope.sources ?? [] };
}

export function AnalystPage() {
  const [params, setParams] = useSearchParams();
  const conversationId = params.get('c');

  const [conversations, setConversations] = useState<ConversationView[]>([]);
  const [archived, setArchived] = useState(false);
  const [filter, setFilter] = useState('');
  const [runs, setRuns] = useState<RunListItem[]>([]);
  const [searches, setSearches] = useState<SearchOption[]>([]);
  const [searchesError, setSearchesError] = useState<string | null>(null);

  const [prompt, setPrompt] = useState('');
  const [scope, setScope] = useState<ScopeDraft>(emptyScope());
  const [scopeLocked, setScopeLocked] = useState(false);
  const [compareOn, setCompareOn] = useState(false);
  const [compareScope, setCompareScope] = useState<ScopeDraft>(emptyScope());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [datasetId, setDatasetId] = useState<string | null>(null);
  const [metricFilters, setMetricFilters] = useState<MetricFilterSet | null>(null);
  /** Dataset già scaricato della conversazione: abilita i filtri sulle metriche per le domande successive. */
  const [conversationDatasetId, setConversationDatasetId] = useState<string | null>(null);

  const loadConversations = useCallback(async () => {
    try {
      setConversations(await analystApi.conversations(archived));
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    }
  }, [archived]);

  const loadRuns = useCallback(async () => {
    if (!conversationId) { setRuns([]); return; }
    try {
      const list = await analystApi.conversationRuns(conversationId);
      setRuns(list);
      const last = list[list.length - 1];
      if (last) {
        setScope(draftOf(last.scope.primary));
        setScopeLocked(true);
        setMetricFilters(last.scope.primary.metricFilters ?? null);
        const view = await analystApi.run(last.id);
        setConversationDatasetId(view.datasetIds?.[0] ?? null);
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    }
  }, [conversationId]);

  useEffect(() => { void loadConversations(); }, [loadConversations]);
  useEffect(() => { void loadRuns(); }, [loadRuns]);
  useEffect(() => {
    analystApi.searches().then(setSearches).catch((err: unknown) => {
      setSearchesError(err instanceof AnalystApiError && err.code === 'pulsar_not_configured'
        ? 'Integrazione Pulsar non configurata: collegala in Impostazioni per analizzare le search (gli export restano disponibili).'
        : err instanceof Error ? err.message : String(err));
    });
  }, []);

  const visible = useMemo(() => conversations.filter((c) => (c.title ?? '').toLowerCase().includes(filter.trim().toLowerCase())), [conversations, filter]);

  const newConversation = () => {
    setParams({});
    setMetricFilters(null);
    setConversationDatasetId(null);
    setScope(emptyScope());
    setScopeLocked(false);
    setCompareOn(false);
    setPrompt('');
  };

  const onChanged = useCallback(() => { void loadConversations(); }, [loadConversations]);
  const onNewRun = useCallback(() => { void loadRuns(); void loadConversations(); }, [loadRuns, loadConversations]);

  const filtersValid = !metricFilters || metricFilters.ranges.every(rangeValid);
  /** Cambiare search o export azzera i filtri: le fonti e le metriche possono essere diverse. */
  const changeScope = (next: ScopeDraft) => {
    const key = (d: ScopeDraft): string => (d.kind === 'dataset' ? `d:${d.datasetId}` : `p:${d.searchId}`);
    if (key(next) !== key(scope)) setMetricFilters(null);
    setScope(next);
  };
  const filtersDatasetId = scope.kind === 'dataset' ? (scope.datasetId || null) : scopeLocked ? conversationDatasetId : null;

  const submit = async () => {
    if (!prompt.trim() || !scopeReady(scope) || (compareOn && !scopeReady(compareScope)) || !filtersValid) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const r = await analystApi.startRun({
        prompt: prompt.trim(),
        scope: { primary: { ...scopeToInput(scope), ...(metricFilters ? { metricFilters } : {}) }, ...(compareOn ? { compare: scopeToInput(compareScope) } : {}) },
        ...(conversationId ? { conversationId } : {}),
      });
      setPrompt('');
      if (r.conversationId !== conversationId) setParams({ c: r.conversationId });
      else await loadRuns();
      await loadConversations();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const openDataset = async () => {
    if (scope.kind === 'dataset') { setDatasetId(scope.datasetId); return; }
    const last = runs[runs.length - 1];
    if (!last) return;
    try {
      const view = await analystApi.run(last.id);
      if (view.datasetIds?.[0]) setDatasetId(view.datasetIds[0]);
      else setSubmitError('Il dataset sarà disponibile dopo lo scarico dei dati.');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    }
  };

  const patchConversation = async (c: ConversationView, patch: { pinned?: boolean; archived?: boolean }) => {
    try {
      await analystApi.updateConversation(c.id, patch);
      await loadConversations();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    }
  };

  const current = conversations.find((c) => c.id === conversationId) ?? null;

  return (
    <div className="flex h-full">
      <aside className="w-64 shrink-0 border-r border-gray-800 flex flex-col">
        <div className="p-3 space-y-2 border-b border-gray-800">
          <button type="button" onClick={newConversation} className="w-full flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-600 text-white text-sm py-2 rounded-lg">
            <MessageSquarePlus className="w-4 h-4" /> Nuova analisi
          </button>
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-gray-500 absolute left-2 top-2" />
            <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Cerca conversazioni"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-7 pr-2 py-1.5 text-xs text-white" />
          </div>
          <button type="button" onClick={() => setArchived(!archived)} className="text-xs text-gray-400 hover:text-white">
            {archived ? '← Conversazioni attive' : 'Archivio'}
          </button>
        </div>
        <ul className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {visible.map((c) => (
            <li key={c.id} className={`group rounded-lg ${c.id === conversationId ? 'bg-gray-800' : 'hover:bg-gray-800/60'}`}>
              <button type="button" onClick={() => setParams({ c: c.id })} className="w-full text-left px-2 py-1.5">
                <span className="flex items-center gap-1 text-sm text-gray-200 truncate">{c.pinned && <Pin className="w-3 h-3 text-blue-400 shrink-0" />}{c.title ?? 'Senza titolo'}</span>
                <span className="block text-[11px] text-gray-500">{c.runs} domande{c.last_status === 'NEEDS_INPUT' ? ' · attende risposta' : ''}</span>
              </button>
              <div className="hidden group-hover:flex gap-2 px-2 pb-1">
                <button type="button" onClick={() => patchConversation(c, { pinned: !c.pinned })} className="text-gray-500 hover:text-white" title={c.pinned ? 'Togli dai fissati' : 'Fissa in alto'}>
                  {c.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                </button>
                <button type="button" onClick={() => patchConversation(c, { archived: !c.archived })} className="text-gray-500 hover:text-white" title={c.archived ? 'Ripristina' : 'Archivia'}>
                  {c.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                </button>
              </div>
            </li>
          ))}
          {visible.length === 0 && <li className="text-xs text-gray-500 px-2 py-4">Nessuna conversazione.</li>}
        </ul>
      </aside>

      <section className="flex-1 flex flex-col min-w-0">
        <header className="flex items-center gap-3 px-6 py-3 border-b border-gray-800">
          <Sparkles className="w-5 h-5 text-blue-400" />
          <h2 className="text-lg font-semibold truncate">{current?.title ?? 'Agente Analista'}</h2>
          {conversationId && runs.length > 0 && (
            <button type="button" onClick={openDataset} className="ml-auto flex items-center gap-1.5 text-xs text-gray-300 border border-gray-700 rounded-lg px-3 py-1.5 hover:bg-gray-800">
              <Database className="w-3.5 h-3.5" /> Dataset
            </button>
          )}
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {!conversationId && (
            <div className="text-sm text-gray-400 max-w-2xl">
              Scegli il perimetro (una search TRAC con il periodo, oppure un export) e fai una domanda in linguaggio naturale:
              l'analista scarica e prepara i dati, calcola le metriche, legge i contenuti e risponde con un report verificato,
              dove ogni numero e ogni citazione vengono dai dati.
            </div>
          )}
          {runs.map((r) => <RunCard key={r.id} runId={r.id} onChanged={onChanged} onNewRun={onNewRun} />)}
        </div>

        <footer className="border-t border-gray-800 px-6 py-4 space-y-3 bg-gray-950/60">
          {scopeLocked ? (
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <span>Perimetro: {describeScope(scope, searches)}</span>
              <button type="button" onClick={() => setScopeLocked(false)} className="text-blue-300 hover:underline">cambia perimetro</button>
            </div>
          ) : (
            <div className="space-y-3">
              <ScopePicker value={scope} onChange={changeScope} searches={searches} searchesError={searchesError} />
              <label className="flex items-center gap-2 text-xs text-gray-400">
                <input type="checkbox" checked={compareOn} onChange={(e) => setCompareOn(e.target.checked)} /> Confronta con un secondo periodo o un'altra search
              </label>
              {compareOn && (
                <div className="border-l-2 border-gray-700 pl-3">
                  <ScopePicker value={compareScope} onChange={setCompareScope} searches={searches} searchesError={null} />
                </div>
              )}
            </div>
          )}
          <MetricFiltersPanel datasetId={filtersDatasetId} value={metricFilters} onChange={setMetricFilters} />
          {!filtersValid && <p className="text-xs text-amber-300">Ogni filtro sulle metriche deve avere almeno un minimo o un massimo, e il minimo non può superare il massimo.</p>}
          <div className="flex gap-2">
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void submit(); }}
              placeholder={runs.length ? 'Domanda di approfondimento sullo stesso perimetro…' : 'Es. Gli italiani sono favorevoli o contrari al ritorno al nucleare?'}
              className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 resize-none" />
            <button type="button" onClick={submit}
              disabled={submitting || !prompt.trim() || !scopeReady(scope) || (compareOn && !scopeReady(compareScope)) || !filtersValid}
              className="self-end bg-blue-700 hover:bg-blue-600 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg px-4 py-2 text-sm flex items-center gap-1.5">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Chiedi
            </button>
          </div>
          {submitError && <p className="text-xs text-red-300">{submitError}</p>}
        </footer>
      </section>

      {datasetId && <DatasetPanel datasetId={datasetId} onClose={() => setDatasetId(null)} />}
    </div>
  );
}
